import React, { useState, useEffect } from 'react';
import { contentService } from '../../services/contentService';
import { FiUpload, FiTrash2, FiEdit2, FiSave, FiX, FiImage } from 'react-icons/fi';

const AdminContents = () => {
  const [carouselImages, setCarouselImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    file: null
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadCarouselImages();
  }, []);

  const loadCarouselImages = async () => {
    try {
      setLoading(true);
      const response = await contentService.getCarouselImages();
      if (response.success) {
        setCarouselImages(response.data);
      }
    } catch (err) {
      setError('Carousel görselleri yüklenirken hata: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('Dosya boyutu 10MB\'dan küçük olmalı');
        return;
      }
      setUploadForm({ ...uploadForm, file });
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      setError('Lütfen bir görsel seçin');
      return;
    }

    try {
      setUploading(true);
      setError('');
      const formData = new FormData();
      formData.append('image', uploadForm.file);
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);

      const response = await contentService.uploadCarouselImage(formData);
      if (response.success) {
        setSuccess('Görsel başarıyla yüklendi');
        setUploadForm({ title: '', description: '', file: null });
        document.getElementById('fileInput').value = '';
        loadCarouselImages();
      }
    } catch (err) {
      setError('Yükleme hatası: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu görseli silmek istediğinizden emin misiniz?')) return;

    try {
      const response = await contentService.deleteCarouselImage(id);
      if (response.success) {
        setSuccess('Görsel silindi');
        loadCarouselImages();
      }
    } catch (err) {
      setError('Silme hatası: ' + err.message);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditForm({
      title: item.title || '',
      description: item.description || '',
      order: item.order || 0,
      file: null
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleEditFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.size <= 10 * 1024 * 1024) {
      setEditForm({ ...editForm, file });
    }
  };

  const handleUpdate = async (id) => {
    try {
      const formData = new FormData();
      if (editForm.file) {
        formData.append('image', editForm.file);
      }
      formData.append('title', editForm.title);
      formData.append('description', editForm.description);
      formData.append('order', editForm.order);

      const response = await contentService.updateCarouselImage(id, formData);
      if (response.success) {
        setSuccess('Görsel güncellendi');
        setEditingId(null);
        setEditForm({});
        loadCarouselImages();
      }
    } catch (err) {
      setError('Güncelleme hatası: ' + err.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">İçerik Yönetimi</h1>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg">
            {success}
          </div>
        )}

        {/* Upload Form */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <FiUpload /> Yeni Carousel Görseli Yükle
          </h2>
          <form onSubmit={handleUpload} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Görsel Dosyası (Max 10MB)
              </label>
              <input
                id="fileInput"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileSelect}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Başlık (Opsiyonel)
              </label>
              <input
                type="text"
                value={uploadForm.title}
                onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Görsel başlığı"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Açıklama (Opsiyonel)
              </label>
              <textarea
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Görsel açıklaması"
                rows="3"
              />
            </div>

            <button
              type="submit"
              disabled={uploading || !uploadForm.file}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <FiUpload />
              {uploading ? 'Yükleniyor...' : 'Yükle'}
            </button>
          </form>
        </div>

        {/* Carousel Images List */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Carousel Görselleri</h2>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Yükleniyor...</p>
            </div>
          ) : carouselImages.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FiImage className="mx-auto text-4xl mb-2" />
              <p>Henüz carousel görseli yok</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {carouselImages.map((item) => (
                <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title || 'Carousel'}
                    className="w-full h-48 object-cover"
                  />

                  {editingId === item.id ? (
                    <div className="p-4 space-y-3">
                      <input
                        type="text"
                        value={editForm.title}
                        onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                        placeholder="Başlık"
                      />
                      <textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                        placeholder="Açıklama"
                        rows="2"
                      />
                      <input
                        type="number"
                        value={editForm.order}
                        onChange={(e) => setEditForm({ ...editForm, order: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                        placeholder="Sıra"
                      />
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleEditFileSelect}
                        className="text-xs"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleUpdate(item.id)}
                          className="flex-1 px-3 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700 flex items-center justify-center gap-1"
                        >
                          <FiSave /> Kaydet
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="flex-1 px-3 py-2 bg-gray-400 text-white rounded text-sm hover:bg-gray-500 flex items-center justify-center gap-1"
                        >
                          <FiX /> İptal
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-800 mb-1">
                        {item.title || 'Başlıksız'}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2">
                        {item.description || 'Açıklama yok'}
                      </p>
                      <p className="text-xs text-gray-500 mb-3">Sıra: {item.order}</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => startEdit(item)}
                          className="flex-1 px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 flex items-center justify-center gap-1"
                        >
                          <FiEdit2 /> Düzenle
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="flex-1 px-3 py-2 bg-red-600 text-white rounded text-sm hover:bg-red-700 flex items-center justify-center gap-1"
                        >
                          <FiTrash2 /> Sil
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminContents;
